import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../components/Icons.jsx';
import { notify } from '@/lib/notifications';
import { PlatformIcon } from './shared/PlatformIcon.jsx';
import {
  PLATFORMS,
  PLATFORM_LABELS,
  FORMAT_OPTIONS,
  GOAL_OPTIONS,
  TONE_OPTIONS,
  VISUAL_STYLE_OPTIONS,
} from './shared/platformLabels.js';
import { fromPublishingForm } from './shared/brandFormUtils.js';
import { api } from '../../services/api/client.js';

const DAY_OPTIONS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export function PublishingRulesTab({ publishingForm, setPublishingForm, platformPrefs, setPlatformPrefs }) {
  const [savingGlobal, setSavingGlobal] = useState(false);

  const update = (key, value) => setPublishingForm((prev) => ({ ...prev, [key]: value }));
  const days = useMemo(
    () => String(publishingForm.preferredDaysText || '').split(',').map((d) => d.trim().toLowerCase()).filter(Boolean),
    [publishingForm.preferredDaysText],
  );
  const toggleDay = (day) => {
    const next = days.includes(day) ? days.filter((d) => d !== day) : [...days, day];
    update('preferredDaysText', next.join(', '));
  };

  async function saveGlobal() {
    try {
      setSavingGlobal(true);
      const result = await api.profile.updatePublishingPreferences(fromPublishingForm(publishingForm));
      const next = result.publishingPreferences;
      setPublishingForm({
        autopilotMode: next.autopilotMode,
        postsPerWeek: next.postsPerWeek,
        preferredDaysText: (next.preferredDays || []).join(', '),
        preferredTimesText: (next.preferredTimes || []).join(', '),
        timezone: next.timezone,
        requireApprovalForSalesContent: next.requireApprovalForSalesContent,
        requireApprovalForNewPlatforms: next.requireApprovalForNewPlatforms,
      });
      notify('publishing.saved');
    } catch (err) {
      notify('publishing.saveFailed', { error: err });
    } finally {
      setSavingGlobal(false);
    }
  }

  return (
    <>
      <div className="card ds-card-pad settings-card">
        <div className="settings-card-header">
          <h3 className="settings-card-title">Global publishing preferences</h3>
          <p className="settings-card-description">Cadence, days, and approval defaults that apply across platforms.</p>
        </div>

        <div className="profile-form-grid">
          <Field label="Autopilot mode" hint="Approval = AI drafts, you confirm. Auto = AI publishes directly.">
            <select className="input select form-input" value={publishingForm.autopilotMode} onChange={(e) => update('autopilotMode', e.target.value)}>
              <option value="approval">Approval first</option>
              <option value="auto">Auto-publish</option>
            </select>
          </Field>
          <Field label="Timezone">
            <input className="input form-input" value={publishingForm.timezone} onChange={(e) => update('timezone', e.target.value)} placeholder="Europe/Kyiv" />
          </Field>
        </div>

        <Field label={`Posts per week — ${publishingForm.postsPerWeek}`} hint="Across all enabled platforms combined">
          <NativeSlider value={Number(publishingForm.postsPerWeek) || 3} onChange={(v) => update('postsPerWeek', v)} min={1} max={21} />
        </Field>

        <Field label="Preferred days">
          <div className="cp-chip-toggle-row">
            {DAY_OPTIONS.map((day) => {
              const on = days.includes(day);
              return (
                <button
                  type="button"
                  key={day}
                  onClick={() => toggleDay(day)}
                  className={'chip cp-chip-toggle ' + (on ? 'chip-active' : '')}
                >
                  {day.slice(0, 3).toUpperCase()}
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Preferred times" hint="HH:MM, comma-separated. e.g. 09:00, 13:30, 18:00">
          <input
            className="input form-input"
            value={publishingForm.preferredTimesText}
            onChange={(e) => update('preferredTimesText', e.target.value)}
            placeholder="10:00, 14:30"
          />
        </Field>

        <div className="cp-divider" />

        <ApprovalRow
          label="Require approval for sales content"
          description="AI drafts go to review when promoting offers."
          checked={publishingForm.requireApprovalForSalesContent}
          onChange={(v) => update('requireApprovalForSalesContent', v)}
        />
        <ApprovalRow
          label="Require approval for new platforms"
          description="First post on a newly connected platform goes to review."
          checked={publishingForm.requireApprovalForNewPlatforms}
          onChange={(v) => update('requireApprovalForNewPlatforms', v)}
        />

        <div className="settings-card-footer">
          <span />
          <button className="btn btn-primary" onClick={saveGlobal} disabled={savingGlobal}>
            <Icon name="check" size={13} /> {savingGlobal ? 'Saving…' : 'Save preferences'}
          </button>
        </div>
      </div>

      <div className="card ds-card-pad settings-card">
        <div className="settings-card-header">
          <h3 className="settings-card-title">Per-platform settings</h3>
          <p className="settings-card-description">Toggle each platform on/off, set autopilot, and tune format mix per channel.</p>
        </div>

        <div className="cp-platform-list">
          {PLATFORMS.map((platform) => (
            <PlatformRow
              key={platform}
              platform={platform}
              prefs={platformPrefs.find((p) => p.platform === platform)}
              setPlatformPrefs={setPlatformPrefs}
            />
          ))}
        </div>
      </div>
    </>
  );
}

function ApprovalRow({ label, description, checked, onChange }) {
  return (
    <div className="ds-setting-row">
      <div className="grow">
        <div style={{ fontWeight: 600 }}>{label}</div>
        <div className="caption muted" style={{ fontSize: 12, marginTop: 2 }}>{description}</div>
      </div>
      <Toggle checked={checked} onChange={onChange} />
    </div>
  );
}

function PlatformRow({ platform, prefs, setPlatformPrefs }) {
  const initial = prefs ?? {
    platform,
    enabled: false,
    autopilotEnabled: false,
    preferredFormats: [],
    excludedContentGoals: [],
    defaultTone: null,
    defaultVisualStyle: null,
    maxPostsPerDay: 1,
    maxPostsPerWeek: 5,
  };
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    setDraft(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(prefs)]);

  const update = (key, value) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      scheduleSave(next);
      return next;
    });
  };

  function scheduleSave(next) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => save(next), 800);
  }

  async function save(next) {
    try {
      setSaving(true);
      const result = await api.profile.updatePlatformPreference({
        platform: next.platform,
        enabled: !!next.enabled,
        autopilotEnabled: !!next.autopilotEnabled,
        preferredFormats: next.preferredFormats || [],
        excludedContentGoals: next.excludedContentGoals || [],
        defaultTone: next.defaultTone || null,
        defaultVisualStyle: next.defaultVisualStyle || null,
        maxPostsPerDay: Number(next.maxPostsPerDay) || null,
        maxPostsPerWeek: Number(next.maxPostsPerWeek) || null,
      });
      const saved = result.platformPreference || next;
      setDraft(saved);
      setPlatformPrefs((prev) => {
        const filtered = prev.filter((p) => p.platform !== platform);
        return [...filtered, saved];
      });
      notify('publishing.platformSaved', { values: { platform: PLATFORM_LABELS[platform] } });
    } catch (err) {
      notify('publishing.platformSaveFailed', { error: err, values: { platform: PLATFORM_LABELS[platform] } });
    } finally {
      setSaving(false);
    }
  }

  const toggleFormat = (fmt) => {
    const set = new Set(draft.preferredFormats || []);
    if (set.has(fmt)) set.delete(fmt); else set.add(fmt);
    update('preferredFormats', Array.from(set));
  };
  const toggleGoal = (goal) => {
    const set = new Set(draft.excludedContentGoals || []);
    if (set.has(goal)) set.delete(goal); else set.add(goal);
    update('excludedContentGoals', Array.from(set));
  };

  return (
    <div className={'cp-platform-row ' + (draft.enabled ? '' : 'is-disabled')}>
      <div className="cp-platform-row-head">
        <div className="row" style={{ gap: 12, alignItems: 'center' }}>
          <PlatformIcon platform={platform} size={20} />
          <div>
            <div style={{ fontWeight: 600 }}>{PLATFORM_LABELS[platform]}</div>
            <div className="caption muted" style={{ fontSize: 12 }}>
              {draft.enabled ? (draft.autopilotEnabled ? 'Autopilot on' : 'Manual') : 'Disabled'}
              {saving && ' · saving…'}
            </div>
          </div>
        </div>
        <Toggle checked={!!draft.enabled} onChange={(v) => update('enabled', v)} label="Enabled" />
      </div>

      {draft.enabled && (
        <div className="cp-platform-row-body">
          <div className="ds-setting-row" style={{ borderTop: 0, paddingTop: 0 }}>
            <div className="grow">
              <div style={{ fontWeight: 600 }}>Autopilot</div>
              <div className="caption muted" style={{ fontSize: 12 }}>Allow AI to publish without approval.</div>
            </div>
            <Toggle checked={!!draft.autopilotEnabled} onChange={(v) => update('autopilotEnabled', v)} />
          </div>

          <div className="profile-form-grid">
            <Field label="Default tone">
              <select className="input select form-input" value={draft.defaultTone || ''} onChange={(e) => update('defaultTone', e.target.value || null)}>
                <option value="">Inherit from brand</option>
                {TONE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Default visual style">
              <select className="input select form-input" value={draft.defaultVisualStyle || ''} onChange={(e) => update('defaultVisualStyle', e.target.value || null)}>
                <option value="">Inherit from brand</option>
                {VISUAL_STYLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Preferred formats">
            <div className="cp-chip-toggle-row">
              {FORMAT_OPTIONS.map((fmt) => {
                const on = (draft.preferredFormats || []).includes(fmt.value);
                return (
                  <button
                    key={fmt.value}
                    type="button"
                    onClick={() => toggleFormat(fmt.value)}
                    className={'chip cp-chip-toggle ' + (on ? 'chip-active' : '')}
                  >
                    {fmt.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Excluded content goals" hint="Goals you don't want this platform to chase">
            <div className="cp-chip-toggle-row">
              {GOAL_OPTIONS.map((goal) => {
                const on = (draft.excludedContentGoals || []).includes(goal.value);
                return (
                  <button
                    key={goal.value}
                    type="button"
                    onClick={() => toggleGoal(goal.value)}
                    className={'chip cp-chip-toggle ' + (on ? 'chip-red' : '')}
                  >
                    {goal.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <div className="profile-form-grid">
            <Field label={`Max posts per day — ${draft.maxPostsPerDay ?? 1}`}>
              <NativeSlider value={Number(draft.maxPostsPerDay) || 1} onChange={(v) => update('maxPostsPerDay', v)} min={1} max={20} />
            </Field>
            <Field label={`Max posts per week — ${draft.maxPostsPerWeek ?? 5}`}>
              <NativeSlider value={Number(draft.maxPostsPerWeek) || 5} onChange={(v) => update('maxPostsPerWeek', v)} min={1} max={100} />
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}

export function Toggle({ checked, onChange, label, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      className={'switch ' + (checked ? 'on' : '')}
    />
  );
}

export function NativeSlider({ value, onChange, min = 0, max = 100, step = 1 }) {
  return (
    <input
      type="range"
      className="cp-slider"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(e) => onChange?.(Number(e.target.value))}
    />
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div className="form-field cp-field">
      <label>
        {label}
        {required && <span className="cp-required"> *</span>}
      </label>
      {children}
      {hint && <div className="helper">{hint}</div>}
    </div>
  );
}
